import React, { useState, useEffect } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Form, Button } from 'semantic-ui-react'
import FWOIteractiveTemplateRow from '../helpers/FWOIteractiveTemplateRow'
import {
  getInitialValues,
  getAddressSchema,
  getPageFields,
  getFieldSchema,
} from '../common'
import {
  FormKey,
  IInteractiveTemplateField,
  IInteractiveTemplatePage,
  IInteractiveTemplateSection,
  InteractiveTemplateFieldType,
} from '../../../models/InteractiveTemplate'
import Conditional from '../../common/Conditional'
import * as Config from '../../../public/external-links'
import Accordion from '../../common/Accordion'
import PageTitle from '../../PageTitle'
import { FormRegex } from '../../../models/Common'

enum Page {
  ReasonForDirection = 'Reason for direction',
  AwardAgreementRequirements = 'Award or agreement requirements',
  LetterDetails = 'Letter details',
}

export const AnnualLeaveNoticeTemplate = (props) => {
  const [initialValues, setInitialValues] = useState({})
  const [validationSchema, setValidationSchema] = useState(null)
  const [showReasonForDirectionPage, setShowReasonForDirectionPage] =
    useState(false)
  const [
    showAwardAgreementRequirementsPage,
    setShowAwardAgreementRequirementsPage,
  ] = useState(false)
  const [showLetterDetailsPage, setShowLetterDetailsPage] = useState(false)
  const [letterDetailsCompleted, setLetterDetailsCompleted] = useState(false)
  const [currentPage, setCurrentPage] = useState(null)
  const [validReasonForDirection, setValidReasonForDirection] = useState(false)
  const [validAwardAgreementRequirements, setValidAwardAgreementRequirements] =
    useState(false)
  const [employmentTerm, setEmploymentTerm] = useState('')
  const [awardRequirement, setAwardRequirement] = useState('')
  const [discussedEmployee, setDiscussedEmployee] = useState('')
  const [reasonForLeave, setReasonForLeave] = useState('')
  const [reasonForShutdown, setReasonForShutdown] = useState('')

  useEffect(() => {
    const values = getInitialValues(props.template)
    setInitialValues(values)
  }, [])

  useEffect(() => {
    setCurrentPage(props.show ? Page.ReasonForDirection : null)
  }, [props.show])

  useEffect(() => {
    const schema = getPageSchema(currentPage)
    setValidationSchema(schema)
    if (currentPage === Page.ReasonForDirection) {
      setShowReasonForDirectionPage(true)
      setShowAwardAgreementRequirementsPage(false)
      setShowLetterDetailsPage(false)
    } else if (currentPage === Page.AwardAgreementRequirements) {
      setShowReasonForDirectionPage(false)
      setShowAwardAgreementRequirementsPage(true)
      setShowLetterDetailsPage(false)
    } else if (currentPage === Page.LetterDetails) {
      setShowReasonForDirectionPage(false)
      setShowAwardAgreementRequirementsPage(false)
      setShowLetterDetailsPage(true)
    }
    window.scrollTo(0, 0)
  }, [currentPage])

  const getPageSchema = (currentPage) => {
    const page = props.template?.pages?.find(
      (page) => page.name === currentPage
    )

    if (!page) {
      return
    }

    const fields = getPageFields(page)

    const schema = fields.reduce((schema, field) => {
      if (field.type === InteractiveTemplateFieldType.Instruction) {
        return schema
      }
      if (field.type === InteractiveTemplateFieldType.Hidden) {
        // skip validation for hidden field
        return schema
      }
      if (field.type === InteractiveTemplateFieldType.Address) {
        return getAddressSchema(schema, field)
      }
      if (field.replacement === 'AWARD_NAME') {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('EMPLOYMENT_TERMS', {
            is: (EMPLOYMENT_TERMS) => EMPLOYMENT_TERMS === 'Award',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }
      if (field.replacement === 'EA_NAME') {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('EMPLOYMENT_TERMS', {
            is: (EMPLOYMENT_TERMS) =>
              EMPLOYMENT_TERMS === 'Enterprise Agreement',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }
      if (field.replacement === 'AWARD_ALLOW') {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('EMPLOYMENT_TERMS', {
            is: (EMPLOYMENT_TERMS) =>
              EMPLOYMENT_TERMS === 'Award' ||
              EMPLOYMENT_TERMS === 'Enterprise Agreement',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }

      if (field.replacement === 'SHUT_REASON') {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('WHY_DIRECTION', {
            is: (WHY_DIRECTION) =>
              WHY_DIRECTION === 'Temporary business shutdown',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }

      if (
        field.replacement === 'START_DATE' ||
        field.replacement === 'END_DATE'
      ) {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('WHY_DIRECTION', {
            is: (WHY_DIRECTION) =>
              WHY_DIRECTION === 'Temporary business shutdown',
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .matches(
                FormRegex.Date,
                'Please enter a valid date in DD/MM/YYYY format'
              ),
          }),
        }
      }

      if (field.replacement === 'OTHER_SPECIFY') {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('SHUT_REASON', {
            is: (SHUT_REASON) => SHUT_REASON === 'Other',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }

      if (
        field.replacement === 'DISCUSSED_EMPLOYEE' ||
        field.replacement === 'EMPLOYEE_BALANCE' ||
        field.replacement === 'HOW_MUCH'
      ) {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('WHY_DIRECTION', {
            is: (WHY_DIRECTION) =>
              WHY_DIRECTION === 'Excessive annual leave balance',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }

      if (
        field.replacement === 'START_LEAVE' ||
        field.replacement === 'END_LEAVE'
      ) {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('WHY_DIRECTION', {
            is: (WHY_DIRECTION) =>
              WHY_DIRECTION === 'Excessive annual leave balance',
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .matches(
                FormRegex.Date,
                'Please enter a valid date in DD/MM/YYYY format'
              ),
          }),
        }
      }

      if (field.replacement === 'DATE_DISCUSS') {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('DISCUSSED_EMPLOYEE', {
            is: (DISCUSSED_EMPLOYEE) => DISCUSSED_EMPLOYEE === 'Yes',
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .matches(
                FormRegex.Date,
                'Please enter a valid date in DD/MM/YYYY format'
              ),
          }),
        }
      }

      return getFieldSchema(schema, field)
    }, {})

    return Yup.object().shape(schema)
  }

  const renderField = (field: IInteractiveTemplateField, index: number) => {
    if (field.replacement === 'WHY_DIRECTION') {
      return (
        <FWOIteractiveTemplateRow
          key={index}
          {...field}
          onSelection={(value) => setReasonForLeave(value?.toLowerCase())}
        ></FWOIteractiveTemplateRow>
      )
    }

    if (field.replacement === 'EMPLOYMENT_TERMS') {
      return (
        <FWOIteractiveTemplateRow
          key={index}
          {...field}
          onSelection={(value) => setEmploymentTerm(value?.toLowerCase())}
        ></FWOIteractiveTemplateRow>
      )
    }

    if (field.replacement === 'AWARD_NAME' && employmentTerm !== 'award') {
      return null
    }
    if (field.replacement === 'AWARD_NAME' && employmentTerm === 'award') {
      field.placeholder = '[Choose one]'
    }
    if (
      field.replacement === 'EA_NAME' &&
      employmentTerm !== 'enterprise agreement'
    ) {
      return null
    }

    if (field.replacement === 'AWARD_ALLOW') {
      if (
        employmentTerm === 'award' ||
        employmentTerm === 'enterprise agreement'
      ) {
        return (
          <FWOIteractiveTemplateRow
            key={index}
            {...field}
            onSelection={(value) => setAwardRequirement(value?.toLowerCase())}
          ></FWOIteractiveTemplateRow>
        )
      }
      return null
    }

    if (field.replacement === 'ALLOW_NO_TIP') {
      if (awardRequirement === 'no') {
        return (
          <div key={index}>
            <p>
              This tool requires you to have an award or enterprise agreement
              that allows you to direct this employee to take annual leave
              before continuing.
            </p>
            <p>
              Check your award or agreement to see if it includes this
              provision, and if there are any other requirements or conditions
              you must fulfill, such as providing notice.
            </p>
            <p>
              <a
                target="_blank"
                href={Config.INTERACTIVE_TEMPLATE_TAKE_ANNUAL_LEAVE}
                rel="noreferrer"
              >
                {' '}
                Find out more about directing an employee to take annual leave.
              </a>
            </p>
          </div>
        )
      }

      return null
    }

    if (field.replacement === 'DISCUSSED_EMPLOYEE') {
      return (
        <FWOIteractiveTemplateRow
          key={index}
          {...field}
          onSelection={(value) => setDiscussedEmployee(value?.toLowerCase())}
        ></FWOIteractiveTemplateRow>
      )
    }

    if (field.replacement === 'DATE_DISCUSS') {
      if (discussedEmployee === 'yes') {
        return (
          <FWOIteractiveTemplateRow
            key={index}
            {...field}
          ></FWOIteractiveTemplateRow>
        )
      }
      return null
    }

    if (field.replacement === 'SHUT_REASON') {
      return (
        <FWOIteractiveTemplateRow
          key={index}
          {...field}
          onSelection={(value) => setReasonForShutdown(value?.toLowerCase())}
        ></FWOIteractiveTemplateRow>
      )
    }

    if (
      field.replacement === 'OTHER_SPECIFY' &&
      reasonForShutdown !== 'other'
    ) {
      return null
    }

    return (
      <FWOIteractiveTemplateRow
        key={index}
        {...field}
      ></FWOIteractiveTemplateRow>
    )
  }

  const validatePage = async (validateForm, setState) => {
    return validateForm().then((errors) => {
      console.log(errors)
      const state = Object.keys(errors).length === 0
      setState(state)
      return state
    })
  }

  const onSubmit = (values) => {
    setInitialValues({ ...initialValues, ...values })
    if (currentPage === Page.ReasonForDirection) {
      setValidReasonForDirection(true)
      if (employmentTerm === 'this employee is award and agreement free') {
        setValidAwardAgreementRequirements(true)
        setCurrentPage(Page.LetterDetails)
      } else {
        setCurrentPage(Page.AwardAgreementRequirements)
      }
    } else if (currentPage === Page.AwardAgreementRequirements) {
      if (
        (employmentTerm === 'award' ||
          employmentTerm === 'enterprise agreement') &&
        awardRequirement !== 'yes'
      ) {
        alert('You cannot continue')
        return
      }
      setValidAwardAgreementRequirements(true)
      setCurrentPage(Page.LetterDetails)
    } else if (currentPage === Page.LetterDetails) {
      if (values['SHUT_REASON'] === 'Other') {
        values['SHUT_REASON'] = values['OTHER_SPECIFY']
      }
      setLetterDetailsCompleted(true)
      props.handleNext(values, FormKey.AnnualLeaveNotice)
      setShowLetterDetailsPage(false)
    }
  }

  const renderPage = (
    page: IInteractiveTemplatePage,
    index: number,
    validateForm
  ) => {
    if (page?.name === Page.ReasonForDirection) {
      return (
        <Accordion
          key={index}
          heading={page.name}
          expanded={showReasonForDirectionPage}
          onClick={() => {
            if (
              !showReasonForDirectionPage &&
              currentPage !== Page.ReasonForDirection
            ) {
              setCurrentPage(Page.ReasonForDirection)
            } else {
              setShowReasonForDirectionPage(!showReasonForDirectionPage)
            }
          }}
        >
          {page.sections?.map((section, index) =>
            renderSection(section, index)
          )}
          <p className="form-submit">
            <Button
              type="submit"
              className="ui is-primary button form-submit"
              onClick={() =>
                validatePage(validateForm, setValidReasonForDirection)
              }
            >
              Next
            </Button>
          </p>
        </Accordion>
      )
    }

    if (page?.name === Page.AwardAgreementRequirements) {
      return (
        <Accordion
          key={index}
          heading={page.name}
          expanded={showAwardAgreementRequirementsPage}
          onClick={() => {
            if (!currentPage) {
              return
            }
            if (showAwardAgreementRequirementsPage) {
              // a user just folds the page
              setShowAwardAgreementRequirementsPage(false)
            } else if (currentPage === Page.ReasonForDirection) {
              // check if page above is valid
              validatePage(validateForm, setValidReasonForDirection).then(
                (isPrevPageValid: boolean) => {
                  if (isPrevPageValid) {
                    setCurrentPage(Page.AwardAgreementRequirements)
                  }
                }
              )
            } else if (currentPage === Page.AwardAgreementRequirements) {
              setShowAwardAgreementRequirementsPage(true)
            } else {
              setCurrentPage(Page.AwardAgreementRequirements)
              setShowAwardAgreementRequirementsPage(true)
            }
          }}
        >
          <Conditional if={validReasonForDirection}>
            {page.sections?.map((section, index) =>
              renderSection(section, index)
            )}
            <p className="form-submit">
              <Button
                type="submit"
                className="ui is-primary button"
                onClick={() =>
                  validatePage(validateForm, setValidAwardAgreementRequirements)
                }
              >
                Next
              </Button>
            </p>
          </Conditional>
        </Accordion>
      )
    }

    if (page?.name === Page.LetterDetails) {
      return (
        <Accordion
          key={index}
          heading={page.name}
          expanded={showLetterDetailsPage}
          onClick={() => {
            if (showLetterDetailsPage) {
              // a user just folds the page
              setShowLetterDetailsPage(false)
            } else if (
              currentPage === Page.ReasonForDirection &&
              !letterDetailsCompleted
            ) {
              return
            } else if (currentPage === Page.AwardAgreementRequirements) {
              if (letterDetailsCompleted) {
                setCurrentPage(Page.LetterDetails)
              } else {
                validatePage(validateForm, null).then(
                  (isPrevPageValid: boolean) => {
                    if (isPrevPageValid || letterDetailsCompleted) {
                      setCurrentPage(Page.LetterDetails)
                    }
                  }
                )
              }
              // check if page above is valid
              validatePage(validateForm, null).then(
                (isPrevPageValid: boolean) => {
                  if (isPrevPageValid || letterDetailsCompleted) {
                    setCurrentPage(Page.LetterDetails)
                  }
                }
              )
            } else {
              setCurrentPage(Page.LetterDetails)
              setShowLetterDetailsPage(true)
            }
          }}
        >
          <Conditional if={validAwardAgreementRequirements}>
            {page.sections?.map((section, index) => {
              if (
                (section.name === 'Temporary business shutdown' &&
                  reasonForLeave !== 'temporary business shutdown') ||
                (section.name === 'Excessive leave balance' &&
                  reasonForLeave !== 'excessive annual leave balance')
              ) {
                return null
              }

              return renderSection(section, index)
            })}
            <p className="form-submit">
              <Button
                type="submit"
                className="ui is-primary button"
                onClick={() => {
                  validateForm().then((errors) => {
                    console.log(errors)
                  })
                }}
              >
                Next
              </Button>
            </p>
          </Conditional>
        </Accordion>
      )
    }
  }

  const renderSection = (
    section: IInteractiveTemplateSection,
    index: number
  ) => {
    if (section?.fields) {
      return (
        <section key={index}>
          <h3>
            {section.name.toLowerCase() !== 'directing employees to take leave'
              ? section.name
              : null}
          </h3>
          {section.fields.map((field, index) => renderField(field, index))}
        </section>
      )
    } else {
      return <></>
    }
  }

  return (
    <>
      <PageTitle title="Notice of requirement to take annual leave - Templates - Fair Work Ombudsman" />

      <Formik
        validationSchema={validationSchema}
        enableReinitialize
        initialValues={initialValues}
        validateOnMount={false}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, validateForm }) => (
          <Form
            className="ui form osp-variant large"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className="accordian">
              {props.template?.pages?.map((page, index) =>
                renderPage(page, index, validateForm)
              )}
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}
